// Booking-page sub-brand hero -- the non-property-trade equivalent of
// HouseProudHero. Sits on per-trade marketing pages where the trade
// doesn't operate on a property (hair stylists, PTs, dog groomers,
// tutors, etc.) but DOES rely on customers self-booking time.
//
// Why this exists: the "Send one link, fill your week" pitch was
// previously a small Card on the per-trade page, which read as a
// footnote. Booking pages are arguably the strongest single feature
// for service-trade audiences (no website, no widget, no app the
// customer downloads), so the section is now a full hero with a
// phone-mock visual showing the customer-side booking flow:
// stylist's name + photo, available slot list, "Book this slot"
// button with optional deposit.
//
// Visual chrome mirrors HouseProudHero (gradient-coloured section,
// big-blur backdrop, two-column copy + visual). Default accent is
// amber to keep the Manos brand front-and-centre, but the caller
// can pick any GradientHeroAccent if a trade page wants its own
// hue family.
import * as React from "react";
import {
  ArrowRight,
  CalendarBlank,
  CheckCircle,
  Clock,
} from "@phosphor-icons/react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { FadeInOnScroll } from "@/components/ui/FadeInOnScroll";

export type BookingPageHeroAccent =
  | "amber"
  | "rose"
  | "violet"
  | "teal"
  | "green"
  | "blue"
  | "orange";

export interface BookingPageHeroProps {
  /** Accent hue family. Default amber so the Manos brand carries.
   *  Override per trade if the trade-page accent is too dominant
   *  alongside the section. */
  accent?: BookingPageHeroAccent;
  /** Trader name shown at the top of the phone mock. e.g. "Linda".
   *  Defaults to a neutral "Sarah" so the mock reads to any
   *  visitor regardless of whether the trade is salon-based or
   *  field-based. */
  traderFirstName?: string;
  /** Tagline shown under the trader name. Default reads "Your hair
   *  stylist" / equivalent depending on caller. */
  traderTagline?: string;
  /** Trade-specific job description on the slot button. Default is
   *  "appointment". */
  slotLabel?: string;
}

// Light accent wash per accent. Iterated through dark-gradient and
// solid-dark; both read as a sub-brand takeover competing with the
// HouseProud blue surface. The trade page already breathes in its
// own colour family via GradientHeroBackdrop on the hero, so this
// section uses a soft light wash + dark text -- quieter, brand-
// consistent, doesn't fight the rest of the page.
const SECTION_BG: Record<BookingPageHeroAccent, string> = {
  amber: "bg-amber-50 dark:bg-amber-950/30",
  rose: "bg-rose-50 dark:bg-rose-950/30",
  violet: "bg-violet-50 dark:bg-violet-950/30",
  teal: "bg-teal-50 dark:bg-teal-950/30",
  green: "bg-green-50 dark:bg-green-950/30",
  blue: "bg-blue-50 dark:bg-blue-950/30",
  orange: "bg-orange-50 dark:bg-orange-950/30",
};

const PILL_BG: Record<BookingPageHeroAccent, string> = {
  amber:
    "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700",
  rose: "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700",
  violet:
    "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-700",
  teal: "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-700",
  green:
    "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-700",
  blue: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700",
  orange:
    "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-700",
};

const PILL_ICON: Record<BookingPageHeroAccent, string> = {
  amber: "text-amber-600 dark:text-amber-400",
  rose: "text-rose-600 dark:text-rose-400",
  violet: "text-violet-600 dark:text-violet-400",
  teal: "text-teal-600 dark:text-teal-400",
  green: "text-green-600 dark:text-green-400",
  blue: "text-blue-600 dark:text-blue-400",
  orange: "text-orange-600 dark:text-orange-400",
};

const HEADLINE_ACCENT: Record<BookingPageHeroAccent, string> = {
  amber: "text-amber-600 dark:text-amber-400",
  rose: "text-rose-600 dark:text-rose-400",
  violet: "text-violet-600 dark:text-violet-400",
  teal: "text-teal-600 dark:text-teal-400",
  green: "text-green-600 dark:text-green-400",
  blue: "text-blue-600 dark:text-blue-400",
  orange: "text-orange-600 dark:text-orange-400",
};

const BLUR_TOP: Record<BookingPageHeroAccent, string> = {
  amber: "bg-amber-200/50 dark:bg-amber-500/10",
  rose: "bg-rose-200/50 dark:bg-rose-500/10",
  violet: "bg-violet-200/50 dark:bg-violet-500/10",
  teal: "bg-teal-200/50 dark:bg-teal-500/10",
  green: "bg-green-200/50 dark:bg-green-500/10",
  blue: "bg-blue-200/50 dark:bg-blue-500/10",
  orange: "bg-orange-200/50 dark:bg-orange-500/10",
};

const PHONE_BUTTON_BG: Record<BookingPageHeroAccent, string> = {
  amber: "bg-amber-500 hover:bg-amber-600",
  rose: "bg-rose-500 hover:bg-rose-600",
  violet: "bg-violet-500 hover:bg-violet-600",
  teal: "bg-teal-500 hover:bg-teal-600",
  green: "bg-green-500 hover:bg-green-600",
  blue: "bg-blue-500 hover:bg-blue-600",
  orange: "bg-orange-500 hover:bg-orange-600",
};

const PHONE_ACCENT_DOT: Record<BookingPageHeroAccent, string> = {
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  violet: "bg-violet-500",
  teal: "bg-teal-500",
  green: "bg-green-500",
  blue: "bg-blue-500",
  orange: "bg-orange-500",
};

const PHONE_AVATAR_RING: Record<BookingPageHeroAccent, string> = {
  amber: "ring-amber-300",
  rose: "ring-rose-300",
  violet: "ring-violet-300",
  teal: "ring-teal-300",
  green: "ring-green-300",
  blue: "ring-blue-300",
  orange: "ring-orange-300",
};

export function BookingPageHero({
  accent = "amber",
  traderFirstName = "Sarah",
  traderTagline = "Independent service professional",
  slotLabel = "appointment",
}: BookingPageHeroProps) {
  return (
    <FadeInOnScroll>
      <section
        className={`py-24 ${SECTION_BG[accent]} text-foreground relative overflow-hidden`}
      >
        {/* Background blur circles for depth -- quieter wash now the
            section background is light. */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className={`absolute -top-40 -right-32 w-[28rem] h-[28rem] rounded-full blur-3xl ${BLUR_TOP[accent]}`}
          />
          <div
            className={`absolute -bottom-32 -left-32 w-96 h-96 rounded-full blur-3xl ${BLUR_TOP[accent]}`}
          />
        </div>

        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left -- copy */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${PILL_BG[accent]}`}
                >
                  <CalendarBlank
                    className={`h-4 w-4 ${PILL_ICON[accent]}`}
                    weight="duotone"
                  />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Bundled with every Manos account
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight text-foreground">
                  Send one link.
                  <br />
                  <span className={HEADLINE_ACCENT[accent]}>
                    Fill your week.
                  </span>
                </h2>
                <p className="text-muted-foreground leading-relaxed max-w-md">
                  Every Manos account comes with a public booking page
                  showing your real availability. Customers pick a slot
                  inside your hours, pay a deposit if you want one, and
                  land straight on your schedule. Zero back-and-forth.
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  "Real-time availability synced from your Manos calendar",
                  "Optional deposit collected at the time of booking",
                  "Drop the link in your bio, on your site, or in a reply",
                  "No widget to install, no extra app for your customers",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-foreground/90"
                  >
                    <CheckCircle
                      className={`h-5 w-5 ${HEADLINE_ACCENT[accent]} shrink-0 mt-0.5`}
                      weight="fill"
                    />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  size="lg"
                  className="h-11 px-7 text-sm bg-amber-600 hover:bg-amber-700 text-white font-bold"
                >
                  <Link to="/sign-up">
                    Start your booking page
                    <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                  </Link>
                </Button>
                <p className="text-xs text-muted-foreground self-center">
                  3 days free. Booking page included.
                </p>
              </div>
            </div>

            {/* Right -- phone-mock visual showing the customer view */}
            <div className="hidden lg:flex justify-center">
              <PhoneMockBookingPage
                accent={accent}
                traderFirstName={traderFirstName}
                traderTagline={traderTagline}
                slotLabel={slotLabel}
              />
            </div>
          </div>
        </div>
      </section>
    </FadeInOnScroll>
  );
}

interface PhoneMockProps {
  accent: BookingPageHeroAccent;
  traderFirstName: string;
  traderTagline: string;
  slotLabel: string;
}

function PhoneMockBookingPage({
  accent,
  traderFirstName,
  traderTagline,
  slotLabel,
}: PhoneMockProps) {
  // Three plausible upcoming slots -- enough to show the picker
  // shape, not so many that the phone fills with rows. The middle
  // slot is "highlighted" as if the customer's tapped it, ready to
  // book.
  const slots = [
    { day: "Tomorrow", time: "10:30am", state: "available" as const },
    { day: "Tomorrow", time: "2:00pm", state: "selected" as const },
    { day: "Thursday", time: "9:00am", state: "available" as const },
  ];

  return (
    <div className="relative">
      {/* Phone outer chrome -- iPhone-style. */}
      <div
        className="relative w-[300px] h-[600px] rounded-3xl border-[6px] border-stone-900 bg-stone-900 shadow-lg shadow-black/20 overflow-hidden"
        aria-hidden="true"
      >
        {/* Status-bar dot row */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1">
          <div className="h-1.5 w-1.5 rounded-full bg-stone-700" />
          <div className="h-5 w-20 rounded-full bg-stone-900" />
          <div className="h-1.5 w-1.5 rounded-full bg-stone-700" />
        </div>

        {/* Inner screen */}
        <div className="absolute inset-2 rounded-2xl bg-white text-stone-900 overflow-hidden flex flex-col">
          {/* Header band -- trader avatar + name + tagline */}
          <div className="px-5 pt-9 pb-4 border-b border-stone-200">
            <div className="flex items-center gap-3">
              {/* Avatar placeholder */}
              <div
                className={`h-12 w-12 rounded-full bg-stone-200 ring-2 ${PHONE_AVATAR_RING[accent]} flex items-center justify-center text-stone-500 font-bold`}
              >
                {traderFirstName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-stone-900 truncate">
                  Book with {traderFirstName}
                </p>
                <p className="text-xs text-stone-500 truncate">
                  {traderTagline}
                </p>
              </div>
            </div>
          </div>

          {/* Picker label */}
          <div className="px-5 pt-4 pb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">
              Pick a time
            </p>
          </div>

          {/* Slot rows */}
          <div className="px-5 space-y-2 flex-1">
            {slots.map((slot) => (
              <SlotRow
                key={slot.day + slot.time}
                day={slot.day}
                time={slot.time}
                selected={slot.state === "selected"}
                accent={accent}
              />
            ))}
          </div>

          {/* CTA button */}
          <div className="px-5 pb-6 space-y-2">
            <button
              type="button"
              className={`w-full h-11 rounded-md text-sm font-bold text-white ${PHONE_BUTTON_BG[accent]}`}
            >
              Book this {slotLabel}
            </button>
            <p className="text-[10px] text-center text-stone-400">
              Confirms instantly. No account needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SlotRow({
  day,
  time,
  selected,
  accent,
}: {
  day: string;
  time: string;
  selected: boolean;
  accent: BookingPageHeroAccent;
}) {
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-md border transition-colors ${
        selected
          ? "border-stone-900 bg-stone-50"
          : "border-stone-200 bg-white"
      }`}
    >
      <Clock
        className={`h-4 w-4 ${
          selected ? "text-stone-900" : "text-stone-400"
        }`}
        weight={selected ? "fill" : "regular"}
      />
      <div className="flex-1">
        <p className="text-xs font-medium text-stone-500">{day}</p>
        <p className="text-sm font-bold text-stone-900">{time}</p>
      </div>
      {selected ? (
        <span
          className={`h-2.5 w-2.5 rounded-full ${PHONE_ACCENT_DOT[accent]}`}
        />
      ) : (
        <span className="text-xs text-stone-400">Free</span>
      )}
    </div>
  );
}
